(function () {
  const selectors = {
    sticky: "[data-bf-sticky]",
    firstProduct: "[data-bf-product-first]",
    countdown: "[data-countdown-target]",
    discountButton: "[data-discount-code]",
    addToCartButton: "[data-bf-add-to-cart]",
    bundleButtons: "[data-bf-add-bundle]",
    quantityWrapper: "[data-bf-quantity]",
    faqItems: ".bf-faq",
  };

  const defaultRoutes = {
    cartAddUrl: "/cart/add.js",
    cartJsonUrl: "/cart.js",
    cartPageUrl: "/cart",
    discountBaseUrl: "/discount",
  };

  const defaultConfig = {
    cartMutationGap: 1500, // Increased from 1000ms for Safari compatibility
    minRequestGap: 8000,
    errorCooldownMs: 45000,
    activePollMs: 15000,
    idlePollMs: 60000,
    cartOpenDelay: 500,
    maxRetries: 2,
    debugMode: false,
  };

  // Cleanup registry for event listeners and timers
  const cleanupRegistry = {
    listeners: [],
    timers: [],
    observers: [],
    abortControllers: [],
  };

  // Cart state tracking to prevent reopening after user closes it
  let lastCartOpenTime = 0;
  let userClosedCart = false;
  const CART_REOPEN_COOLDOWN = 3000; // 3 seconds cooldown after cart opens

  function getBlackFridayConfig() {
    return { ...defaultConfig, ...(window.BlackFridayConfig || {}) };
  }

  function debugLog(...args) {
    if (getBlackFridayConfig().debugMode) {
      console.log("[BF Debug]", ...args);
    }
  }

  // Rate limiting for cart mutations
  let lastCartMutationTs = 0;

  async function throttledCartMutation(mutationFn) {
    const config = getBlackFridayConfig();
    const now = Date.now();
    const elapsed = now - lastCartMutationTs;
    if (elapsed < config.cartMutationGap) {
      const waitTime = config.cartMutationGap - elapsed;
      debugLog(`Throttling cart mutation for ${waitTime}ms`);
      await new Promise((r) => setTimeout(r, waitTime));
    }
    lastCartMutationTs = Date.now();
    return mutationFn();
  }

  function getRoute(key) {
    const configRoutes = getBlackFridayConfig().routes || {};
    return configRoutes[key] || defaultRoutes[key];
  }

  function buildDiscountRequestUrl(discountCode) {
    const normalizedBase =
      (getRoute("discountBaseUrl") || defaultRoutes.discountBaseUrl).replace(
        /\/$/,
        "",
      );
    const redirectPath = getRoute("cartPageUrl") || defaultRoutes.cartPageUrl;
    const encodedCode = encodeURIComponent(discountCode);
    const encodedRedirect = encodeURIComponent(redirectPath);
    return `${normalizedBase}/${encodedCode}?redirect=${encodedRedirect}`;
  }

  function registerCleanup(type, item) {
    if (cleanupRegistry[type]) {
      cleanupRegistry[type].push(item);
    }
  }

  function cleanup() {
    debugLog("Running cleanup...");
    
    // Clear timers
    cleanupRegistry.timers.forEach((timer) => clearTimeout(timer));
    cleanupRegistry.timers.forEach((timer) => clearInterval(timer));
    
    // Remove event listeners
    cleanupRegistry.listeners.forEach(({ target, event, handler, options }) => {
      target.removeEventListener(event, handler, options);
    });
    
    // Disconnect observers
    cleanupRegistry.observers.forEach((observer) => observer.disconnect());
    
    // Abort fetch requests
    cleanupRegistry.abortControllers.forEach((controller) => controller.abort());
    
    // Clear registry
    Object.keys(cleanupRegistry).forEach((key) => {
      cleanupRegistry[key] = [];
    });
  }

  function toggleStickyVisibility(stickyEl, firstProduct) {
    if (!stickyEl) return;
    const hero = document.querySelector(".bf-hero");
    const heroHeight = hero ? hero.offsetHeight : 0;
    let hasUnlockedSticky = !firstProduct;
    
    const update = () => {
      const shouldShow = hasUnlockedSticky && window.scrollY > heroHeight;
      stickyEl.classList.toggle("is-visible", shouldShow);
    };
    
    update();
    
    const scrollHandler = () => update();
    window.addEventListener("scroll", scrollHandler, { passive: true });
    registerCleanup("listeners", {
      target: window,
      event: "scroll",
      handler: scrollHandler,
      options: { passive: true },
    });

    if (firstProduct) {
      // Optimized threshold array creation
      const observerThresholds = Array.from({ length: 11 }, (_, i) => i / 10);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const hasScrolledPast = entry.intersectionRatio <= 0;
            if (hasScrolledPast && !hasUnlockedSticky) {
              hasUnlockedSticky = true;
              update();
              observer.disconnect();
            }
          });
        },
        {
          rootMargin: "-300px",
          threshold: observerThresholds,
        },
      );

      observer.observe(firstProduct);
      registerCleanup("observers", observer);
    }
  }

  function pad(num) {
    return String(num).padStart(2, "0");
  }

  function initialiseCountdowns() {
    const countdownContainers = document.querySelectorAll(selectors.countdown);
    countdownContainers.forEach((container) => {
      const targetValue = container.getAttribute("data-countdown-target");
      if (!targetValue) return;
      
      const targetDate = new Date(targetValue);
      if (Number.isNaN(targetDate.getTime())) {
        debugLog("Invalid countdown date:", targetValue);
        return;
      }

      // Check if date is in the past
      if (targetDate.getTime() < Date.now()) {
        debugLog("Countdown date is in the past:", targetValue);
        // Still show countdown at 00:00:00:00 instead of returning
      }

      const parts = {
        days: container.querySelector('[data-countdown-part="days"]'),
        hours: container.querySelector('[data-countdown-part="hours"]'),
        minutes: container.querySelector('[data-countdown-part="minutes"]'),
        seconds: container.querySelector('[data-countdown-part="seconds"]'),
      };

      const update = () => {
        const now = new Date();
        let diff = targetDate.getTime() - now.getTime();
        if (diff <= 0) {
          diff = 0;
          clearInterval(timer);
          debugLog("Countdown completed for:", targetValue);
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);

        if (parts.days) parts.days.textContent = pad(days);
        if (parts.hours) parts.hours.textContent = pad(hours);
        if (parts.minutes) parts.minutes.textContent = pad(minutes);
        if (parts.seconds) parts.seconds.textContent = pad(seconds);
      };

      update();
      const timer = setInterval(update, 1000);
      registerCleanup("timers", timer);
    });
  }

  function initialiseDiscountCopy() {
    const buttons = document.querySelectorAll(selectors.discountButton);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      const prefixSpan = button.querySelector("span");
      const originalLabel = prefixSpan ? prefixSpan.textContent : "";
      const successLabel =
        button.getAttribute("data-copy-success") || "Copied!";
      button.setAttribute("aria-live", "polite");

      const reset = () => {
        if (prefixSpan) {
          prefixSpan.textContent = originalLabel;
        }
        button.classList.remove("is-copied");
      };

      const clickHandler = () => {
        const code = button.getAttribute("data-discount-code");
        if (!code) return;

        const applySuccessState = () => {
          if (prefixSpan) {
            prefixSpan.textContent = successLabel;
          }
          button.classList.add("is-copied");
          const resetTimer = setTimeout(reset, 2000);
          registerCleanup("timers", resetTimer);
          debugLog("Discount code copied:", code);
        };

        const applyFailureState = () => {
          debugLog("Failed to copy discount code:", code);
          // Optionally show user-facing error
          if (prefixSpan) {
            prefixSpan.textContent = "Copy failed";
            button.classList.add("is-error");
            const resetTimer = setTimeout(() => {
              reset();
              button.classList.remove("is-error");
            }, 2000);
            registerCleanup("timers", resetTimer);
          }
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(code)
            .then(applySuccessState)
            .catch(() => {
              fallbackCopy(code, applySuccessState, applyFailureState);
            });
        } else {
          fallbackCopy(code, applySuccessState, applyFailureState);
        }
      };

      button.addEventListener("click", clickHandler);
      registerCleanup("listeners", {
        target: button,
        event: "click",
        handler: clickHandler,
      });
    });
  }

  function fallbackCopy(text, onSuccess, onFailure) {
    // Check if execCommand is supported (deprecated but still works in most browsers)
    if (!document.queryCommandSupported || !document.queryCommandSupported("copy")) {
      debugLog("execCommand('copy') not supported");
      if (typeof onFailure === "function") onFailure();
      return;
    }

    const tempInput = document.createElement("textarea");
    tempInput.value = text;
    tempInput.setAttribute("readonly", "");
    tempInput.style.position = "absolute";
    tempInput.style.left = "-9999px";
    document.body.appendChild(tempInput);
    tempInput.select();
    
    try {
      const successful = document.execCommand("copy");
      if (successful && typeof onSuccess === "function") {
        onSuccess();
      } else if (!successful && typeof onFailure === "function") {
        onFailure();
      }
    } catch (err) {
      debugLog("Could not copy discount code", err);
      if (typeof onFailure === "function") onFailure();
    }
    
    document.body.removeChild(tempInput);
  }

  // Shared cart add function to reduce duplication
  async function performCartAdd(formData, retryCount = 0) {
    const config = getBlackFridayConfig();
    
    const doFetch = () =>
      fetch(getRoute("cartAddUrl"), {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        credentials: "include",
        body: formData,
      });

    const response = await throttledCartMutation(doFetch);

    if (response.status === 429 && retryCount < config.maxRetries) {
      const retryAfter =
        parseInt(response.headers.get("Retry-After"), 10) || 2;
      debugLog(`Rate limited (429), retrying after ${retryAfter}s...`);
      await new Promise((r) => setTimeout(r, retryAfter * 1000));
      return performCartAdd(formData, retryCount + 1);
    }

    if (!response.ok) {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        const errorMsg = data?.description || "Unable to add to cart";
        debugLog("Cart add failed:", errorMsg);
        throw new Error(`Failed to add to cart: ${errorMsg}`);
      }
      const errorMsg = `Request failed with status ${response.status}`;
      debugLog("Cart add failed:", errorMsg);
      throw new Error(errorMsg);
    }

    // Shopify may return JSON with varying content-types; try parsing regardless
    try {
      return await response.json();
    } catch {
      // Response OK but not JSON - cart was updated, return minimal object
      return { ok: true };
    }
  }

  async function addVariantToCart(variantId, quantity) {
    const formData = new FormData();
    formData.append("items[0][id]", variantId);
    formData.append(
      "items[0][quantity]",
      Number.isNaN(Number(quantity)) ? 1 : Number(quantity),
    );
    
    debugLog("Adding variant to cart:", { variantId, quantity });
    return performCartAdd(formData);
  }

  async function addMultipleVariantsToCart(variantIds) {
    // Validate variant IDs
    const validIds = variantIds.filter((id) => {
      const isValid = /^\d+$/.test(String(id).trim());
      if (!isValid) {
        debugLog("Invalid variant ID:", id);
      }
      return isValid;
    });

    if (validIds.length === 0) {
      throw new Error("No valid variant IDs provided");
    }

    const formData = new FormData();
    validIds.forEach((id, index) => {
      formData.append(`items[${index}][id]`, id);
      formData.append(`items[${index}][quantity]`, 1);
    });

    debugLog("Adding bundle to cart:", validIds);
    return performCartAdd(formData);
  }

  async function applyDiscountCode(discountCode) {
    if (!discountCode) return Promise.resolve();
    
    debugLog("Applying discount code:", discountCode);
    const discountUrl = buildDiscountRequestUrl(discountCode);
    
    try {
      await fetch(discountUrl, {
        credentials: "same-origin",
      });
      debugLog("Discount code applied successfully");
    } catch (error) {
      debugLog("Discount code could not be applied automatically.", error);
      console.warn("Discount code could not be applied automatically.", error);
      // Don't throw - discount failure shouldn't block cart add
    }
  }

  function openAssortionSideCart() {
    // Check if user recently closed the cart
    const now = Date.now();
    const timeSinceLastOpen = now - lastCartOpenTime;
    
    if (userClosedCart && timeSinceLastOpen < CART_REOPEN_COOLDOWN) {
      debugLog(`Cart not opened - user closed it ${timeSinceLastOpen}ms ago (cooldown: ${CART_REOPEN_COOLDOWN}ms)`);
      return false;
    }

    try {
      if (
        window.Assortion &&
        window.Assortion.cart &&
        typeof window.Assortion.cart.open === "function"
      ) {
        window.Assortion.cart.open();
        lastCartOpenTime = Date.now();
        userClosedCart = false;
        debugLog("Opened Assortion cart via Assortion.cart.open()");
        
        // Reset userClosedCart flag after cooldown period
        const resetTimer = setTimeout(() => {
          userClosedCart = false;
          debugLog("Cart close flag reset");
        }, CART_REOPEN_COOLDOWN);
        registerCleanup("timers", resetTimer);
        
        return true;
      }

      if (window.Assortion && typeof window.Assortion.open === "function") {
        window.Assortion.open();
        lastCartOpenTime = Date.now();
        userClosedCart = false;
        debugLog("Opened Assortion cart via Assortion.open()");
        
        // Reset userClosedCart flag after cooldown period
        const resetTimer = setTimeout(() => {
          userClosedCart = false;
          debugLog("Cart close flag reset");
        }, CART_REOPEN_COOLDOWN);
        registerCleanup("timers", resetTimer);
        
        return true;
      }

      const cartTrigger = document.querySelector(
        ".site-header__icon.site-header__cart",
      );
      if (cartTrigger) {
        cartTrigger.click();
        lastCartOpenTime = Date.now();
        userClosedCart = false;
        debugLog("Opened cart via .site-header__icon.site-header__cart");
        
        // Reset userClosedCart flag after cooldown period
        const resetTimer = setTimeout(() => {
          userClosedCart = false;
          debugLog("Cart close flag reset");
        }, CART_REOPEN_COOLDOWN);
        registerCleanup("timers", resetTimer);
        
        return true;
      }

      const cartButton = document.querySelector(
        '[data-cart-toggle], [data-action="toggle-cart"], .header__icon--cart',
      );
      if (cartButton) {
        cartButton.dispatchEvent(new Event("click", { bubbles: true }));
        lastCartOpenTime = Date.now();
        userClosedCart = false;
        debugLog("Opened cart via fallback cart button");
        
        // Reset userClosedCart flag after cooldown period
        const resetTimer = setTimeout(() => {
          userClosedCart = false;
          debugLog("Cart close flag reset");
        }, CART_REOPEN_COOLDOWN);
        registerCleanup("timers", resetTimer);
        
        return true;
      }

      debugLog("Could not find any cart trigger");
      console.warn("Assortion side cart could not be opened automatically.");
      return false;
    } catch (error) {
      debugLog("Failed to open Assortion side cart:", error);
      console.warn("Failed to open Assortion side cart.", error);
      return false;
    }
  }

  // Shared add-to-cart handler to reduce duplication
  async function handleAddToCart(button, variantData, isBundle = false) {
    const config = getBlackFridayConfig();
    const discountCode = button.getAttribute("data-discount-code") || "";
    const originalText = button.textContent;
    const liveRegion = button.getAttribute("aria-live") ? button : null;

    button.disabled = true;
    button.classList.add("is-loading");
    button.textContent = window.themeStrings?.addingToCart || "Adding…";

    try {
      // Add to cart
      if (isBundle) {
        await addMultipleVariantsToCart(variantData);
      } else {
        await addVariantToCart(variantData.variantId, variantData.quantity);
      }

      // Apply discount code (non-blocking)
      await applyDiscountCode(discountCode.trim());

      // Update cart count
      if (window.updateBlackFridayCartCount) {
        window.updateBlackFridayCartCount();
      }

      // Announce success to screen readers
      if (liveRegion) {
        const announcement = document.createElement("span");
        announcement.className = "sr-only";
        announcement.textContent = "Item added to cart";
        liveRegion.appendChild(announcement);
        setTimeout(() => announcement.remove(), 1000);
      }

      // Open cart after delay
      const openTimer = setTimeout(() => {
        openAssortionSideCart();
      }, config.cartOpenDelay);
      registerCleanup("timers", openTimer);

      debugLog("Add to cart successful");
    } catch (error) {
      debugLog("Add to cart failed:", error);
      console.warn("Add to cart failed.", error);
      
      // Show user-facing error
      button.textContent = "Failed - Try again";
      button.classList.add("is-error");
      
      // Announce error to screen readers
      if (liveRegion) {
        const announcement = document.createElement("span");
        announcement.className = "sr-only";
        announcement.textContent = `Error: ${error.message}`;
        liveRegion.appendChild(announcement);
        setTimeout(() => announcement.remove(), 1000);
      }
      
      const errorTimer = setTimeout(() => {
        button.classList.remove("is-error");
        button.textContent = originalText;
        button.disabled = false;
      }, 3000);
      registerCleanup("timers", errorTimer);
      return;
    }

    button.disabled = false;
    button.classList.remove("is-loading");
    button.textContent = originalText;
  }

  function initialiseProductAddToCart() {
    const buttons = document.querySelectorAll(selectors.addToCartButton);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      // Add accessibility attributes
      if (!button.hasAttribute("aria-label")) {
        button.setAttribute("aria-label", "Add to cart");
      }
      button.setAttribute("aria-live", "polite");

      const clickHandler = async (event) => {
        event.preventDefault();
        if (button.disabled) return;

        const variantId = button.getAttribute("data-variant-id");
        if (!variantId) {
          debugLog("Variant ID missing for add-to-cart button");
          console.warn("Variant ID missing for add-to-cart button.");
          return;
        }

        const quantityInputId = button.getAttribute("data-quantity-input-id");
        let quantityFromInput = null;

        if (quantityInputId) {
          const quantityInput = document.getElementById(quantityInputId);
          if (quantityInput) {
            const parsedValue = parseInt(quantityInput.value, 10);
            const maxQty = parseInt(quantityInput.getAttribute("max"), 10);
            
            if (!Number.isNaN(parsedValue) && parsedValue > 0) {
              // Check max quantity if specified
              if (!Number.isNaN(maxQty) && parsedValue > maxQty) {
                quantityInput.value = String(maxQty);
                quantityFromInput = maxQty;
                debugLog(`Quantity capped at max: ${maxQty}`);
              } else {
                quantityFromInput = parsedValue;
              }
            } else {
              quantityInput.value = "1";
            }
          }
        }

        const quantity = String(
          quantityFromInput ??
            (parseInt(button.getAttribute("data-quantity"), 10) || 1),
        );

        await handleAddToCart(button, { variantId, quantity }, false);
      };

      button.addEventListener("click", clickHandler);
      registerCleanup("listeners", {
        target: button,
        event: "click",
        handler: clickHandler,
      });
    });
  }

  function initialiseQuantityControls() {
    const quantityBlocks = document.querySelectorAll(selectors.quantityWrapper);
    if (!quantityBlocks.length) return;

    quantityBlocks.forEach((block) => {
      const input = block.querySelector("[data-bf-quantity-input]");
      if (!input) return;

      const decrement = block.querySelector("[data-bf-quantity-decrement]");
      const increment = block.querySelector("[data-bf-quantity-increment]");
      const min = Math.max(1, parseInt(input.getAttribute("min") || "1", 10));
      const max = parseInt(input.getAttribute("max"), 10);
      const step = Math.max(1, parseInt(input.getAttribute("step") || "1", 10));

      // Add accessibility labels
      if (decrement && !decrement.hasAttribute("aria-label")) {
        decrement.setAttribute("aria-label", "Decrease quantity");
      }
      if (increment && !increment.hasAttribute("aria-label")) {
        increment.setAttribute("aria-label", "Increase quantity");
      }

      const getCurrentValue = () => parseInt(input.value, 10);
      const setValue = (value) => {
        let safeValue = Number.isNaN(value) ? min : Math.max(min, value);
        // Apply max limit if specified
        if (!Number.isNaN(max)) {
          safeValue = Math.min(safeValue, max);
        }
        input.value = safeValue;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      };

      if (decrement) {
        const decrementHandler = () => {
          setValue(getCurrentValue() - step);
        };
        decrement.addEventListener("click", decrementHandler);
        registerCleanup("listeners", {
          target: decrement,
          event: "click",
          handler: decrementHandler,
        });
      }

      if (increment) {
        const incrementHandler = () => {
          setValue(getCurrentValue() + step);
        };
        increment.addEventListener("click", incrementHandler);
        registerCleanup("listeners", {
          target: increment,
          event: "click",
          handler: incrementHandler,
        });
      }

      const changeHandler = () => {
        setValue(getCurrentValue());
      };
      input.addEventListener("change", changeHandler);
      registerCleanup("listeners", {
        target: input,
        event: "change",
        handler: changeHandler,
      });
    });
  }

  function initialiseBundleAddToCart() {
    const buttons = document.querySelectorAll(selectors.bundleButtons);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      // Add accessibility attributes
      if (!button.hasAttribute("aria-label")) {
        button.setAttribute("aria-label", "Add bundle to cart");
      }
      button.setAttribute("aria-live", "polite");

      const clickHandler = async (event) => {
        event.preventDefault();
        if (button.disabled) return;

        const variantIdsString = button.getAttribute("data-variant-ids");
        if (!variantIdsString) {
          debugLog("Variant IDs missing for bundle button");
          return;
        }

        const variantIds = variantIdsString.split(",").filter(Boolean);
        await handleAddToCart(button, variantIds, true);
      };

      button.addEventListener("click", clickHandler);
      registerCleanup("listeners", {
        target: button,
        event: "click",
        handler: clickHandler,
      });
    });
  }

  function initialiseStickyCart() {
    const cartButton = document.querySelector("[data-bf-sticky-cart]");
    const countBadge = document.querySelector("[data-bf-cart-count]");

    if (!cartButton) return;

    // Add accessibility label
    if (!cartButton.hasAttribute("aria-label")) {
      cartButton.setAttribute("aria-label", "Open shopping cart");
    }

    const config = getBlackFridayConfig();
    const MIN_REQUEST_GAP = config.minRequestGap;
    const ERROR_COOLDOWN_MS = config.errorCooldownMs;
    const ACTIVE_POLL_MS = config.activePollMs;
    const IDLE_POLL_MS = config.idlePollMs;

    let lastRequestTs = 0;
    let inFlight = false;
    let queued = false;
    let cooldownUntil = 0;
    let debounceTimer = null;
    let pollTimer = null;
    let currentAbortController = null;

    const updateCount = async () => {
      try {
        // Cancel previous request if still in flight
        if (currentAbortController) {
          currentAbortController.abort();
        }

        currentAbortController = new AbortController();
        registerCleanup("abortControllers", currentAbortController);

        const res = await fetch(getRoute("cartJsonUrl"), {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          credentials: "include",
          signal: currentAbortController.signal,
        });

        if (!res.ok) {
          if (res.status === 429) {
            cooldownUntil = Date.now() + ERROR_COOLDOWN_MS;
            debugLog(`Rate limited on cart fetch, cooling down for ${ERROR_COOLDOWN_MS}ms`);
          }
          throw new Error(`Cart request failed with status ${res.status}`);
        }

        const cart = await res.json();
        if (countBadge) {
          countBadge.textContent = cart.item_count;
          countBadge.classList.toggle("is-hidden", cart.item_count === 0);
          
          // Update aria-label with count
          if (cartButton) {
            cartButton.setAttribute(
              "aria-label",
              `Open shopping cart, ${cart.item_count} ${cart.item_count === 1 ? "item" : "items"}`,
            );
          }
        }
        debugLog("Cart count updated:", cart.item_count);
      } catch (err) {
        if (err.name === "AbortError") {
          debugLog("Cart fetch aborted");
          return;
        }
        debugLog("Failed to fetch cart count:", err);
        console.warn("Failed to fetch cart count", err);
      } finally {
        lastRequestTs = Date.now();
        inFlight = false;
        currentAbortController = null;
        if (queued) {
          queued = false;
          scheduleUpdate("queued");
        }
      }
    };

    const scheduleUpdate = (reason = "manual") => {
      const now = Date.now();
      if (now < cooldownUntil) {
        debugLog(`Skipping update (in cooldown), reason: ${reason}`);
        return;
      }
      if (document.hidden && reason === "poll") {
        debugLog("Skipping poll update (document hidden)");
        return;
      }
      if (inFlight) {
        queued = true;
        debugLog(`Update queued, reason: ${reason}`);
        return;
      }

      const elapsed = now - lastRequestTs;
      if (elapsed < MIN_REQUEST_GAP) {
        clearTimeout(debounceTimer);
        const waitTime = MIN_REQUEST_GAP - elapsed;
        debugLog(`Debouncing update for ${waitTime}ms, reason: ${reason}`);
        debounceTimer = setTimeout(
          () => scheduleUpdate(reason),
          waitTime,
        );
        registerCleanup("timers", debounceTimer);
        return;
      }

      debugLog(`Updating cart count, reason: ${reason}`);
      inFlight = true;
      updateCount();
    };

    const startPolling = () => {
      const interval = document.hidden ? IDLE_POLL_MS : ACTIVE_POLL_MS;
      clearTimeout(pollTimer);
      pollTimer = setTimeout(() => {
        scheduleUpdate("poll");
        startPolling();
      }, interval);
      registerCleanup("timers", pollTimer);
      debugLog(`Polling started, interval: ${interval}ms`);
    };

    // Initial fetch
    scheduleUpdate("init");

    // Expose update function globally
    window.updateBlackFridayCartCount = () => scheduleUpdate("external");

    // Listen for custom cart update events
    const cartUpdatedHandler = () => scheduleUpdate("event");
    const cartRefreshHandler = () => scheduleUpdate("event");
    
    document.addEventListener("cart:updated", cartUpdatedHandler);
    document.addEventListener("cart:refresh", cartRefreshHandler);
    
    registerCleanup("listeners", {
      target: document,
      event: "cart:updated",
      handler: cartUpdatedHandler,
    });
    registerCleanup("listeners", {
      target: document,
      event: "cart:refresh",
      handler: cartRefreshHandler,
    });

    // Refresh when user returns to the tab
    const visibilityChangeHandler = () => {
      if (!document.hidden) {
        scheduleUpdate("visible");
      }
      startPolling();
    };
    const focusHandler = () => scheduleUpdate("focus");
    
    document.addEventListener("visibilitychange", visibilityChangeHandler);
    window.addEventListener("focus", focusHandler);
    
    registerCleanup("listeners", {
      target: document,
      event: "visibilitychange",
      handler: visibilityChangeHandler,
    });
    registerCleanup("listeners", {
      target: window,
      event: "focus",
      handler: focusHandler,
    });

    // Poll while respecting rate limits; slower cadence while tab is hidden
    startPolling();

    const cartClickHandler = (e) => {
      e.preventDefault();
      openAssortionSideCart();
    };
    
    cartButton.addEventListener("click", cartClickHandler);
    registerCleanup("listeners", {
      target: cartButton,
      event: "click",
      handler: cartClickHandler,
    });
  }

  function initialiseFAQs() {
    const faqItems = document.querySelectorAll(selectors.faqItems);
    if (!faqItems.length) return;

    // Remove custom click handler - let native <details> behavior work
    // This was causing issues with the default toggle behavior
    debugLog(`Found ${faqItems.length} FAQ items (using native behavior)`);
  }

  // Expose cleanup function globally for manual cleanup if needed
  window.cleanupBlackFriday = cleanup;

  document.addEventListener("DOMContentLoaded", () => {
    debugLog("Initializing Black Friday components...");
    
    toggleStickyVisibility(
      document.querySelector(selectors.sticky),
      document.querySelector(selectors.firstProduct),
    );
    initialiseCountdowns();
    initialiseDiscountCopy();
    initialiseQuantityControls();
    initialiseProductAddToCart();
    initialiseBundleAddToCart();
    initialiseStickyCart();
    initialiseFAQs();
    
    // Listen for Assortion cart close events to prevent reopening
    const detectCartClose = () => {
      // Check if cart drawer/overlay is closed
      const cartDrawer = document.querySelector('[data-assortion-cart], .assortion-cart, #assortion-cart');
      const cartOverlay = document.querySelector('.assortion-overlay, [data-assortion-overlay]');
      
      if (cartDrawer || cartOverlay) {
        const observer = new MutationObserver(() => {
          const isCartVisible = cartDrawer?.classList.contains('is-visible') || 
                               cartDrawer?.classList.contains('active') ||
                               cartOverlay?.classList.contains('is-visible') ||
                               cartOverlay?.classList.contains('active');
          
          if (isCartVisible === false && lastCartOpenTime > 0) {
            userClosedCart = true;
            debugLog("User closed cart - setting cooldown");
          }
        });
        
        if (cartDrawer) {
          observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
          registerCleanup("observers", observer);
        }
        if (cartOverlay) {
          observer.observe(cartOverlay, { attributes: true, attributeFilter: ['class'] });
          registerCleanup("observers", observer);
        }
      }
    };
    
    // Try to detect cart close after a short delay (wait for Assortion to initialize)
    setTimeout(detectCartClose, 1000);
    
    debugLog("Black Friday initialization complete");
  });

  // Cleanup on page unload
  window.addEventListener("beforeunload", cleanup);
})();
